export type ReportAnalysisJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export type ReportAnalysisInventoryCell = {
  month: string;
  queryId: string;
  objectCount: number;
  bytes?: number;
  grainLabel?: string;
};

export type ReportAnalysisInventory = {
  startMonth: string;
  endMonth: string;
  cells: ReportAnalysisInventoryCell[];
  missing: ReportAnalysisInventoryCell[];
};

export type ReportAnalysisJob = {
  id: string;
  profileId: string;
  source: string;
  startMonth: string;
  endMonth: string;
  status: ReportAnalysisJobStatus;
  inventory: ReportAnalysisInventoryCell[];
  factsSummary: Record<string, unknown>;
  xlsxReady: boolean;
  pdfReady: boolean;
  pdfStatus: string;
  emailStatus: string;
  recipientEmail: string | null;
  error: string | null;
  createdAt: string | null;
  finishedAt: string | null;
};

export const ANALYSIS_GRAIN_LABELS: Record<string, string> = {
  core_performance: 'Campaign / targeting',
  advertised_asin_daily: 'Advertised product',
  placement_daily: 'Placement',
  search_term_daily: 'Search term',
};

export function formatYearMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Last complete calendar months, matching backend default_month_window (max 4). */
export function defaultAnalysisMonthWindow(
  today = new Date(),
  maxMonths = 4
): { startMonth: string; endMonth: string } {
  const end = new Date(today.getFullYear(), today.getMonth(), 0);
  const start = new Date(end.getFullYear(), end.getMonth() - (maxMonths - 1), 1);
  return { startMonth: formatYearMonth(start), endMonth: formatYearMonth(end) };
}

export function analysisMonthCount(startMonth: string, endMonth: string): number {
  const start = parseYearMonth(startMonth);
  const end = parseYearMonth(endMonth);
  if (!start || !end) return 0;
  return (end.year - start.year) * 12 + (end.month - start.month) + 1;
}

export function parseYearMonth(value: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || month < 1 || month > 12) return null;
  return { year, month };
}

export function normalizeInventoryCell(raw: Record<string, unknown>): ReportAnalysisInventoryCell {
  const queryId = String(raw.queryId ?? raw.query_id ?? '');
  return {
    month: String(raw.month ?? ''),
    queryId,
    objectCount: Number(raw.objectCount ?? raw.object_count ?? 0) || 0,
    bytes: Number(raw.bytes ?? 0) || 0,
    grainLabel: String(raw.grainLabel ?? raw.grain_label ?? ANALYSIS_GRAIN_LABELS[queryId] ?? queryId),
  };
}

export type ReportAnalysisConfig = {
  profileId: string;
  brandNames: string[];
  competitorNames: string[];
  accountName: string;
};

/** Trim, lowercase, drop empties/duplicates — matches Analysis TokenChips. */
export function normalizeNameList(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    if (typeof raw !== 'string') continue;
    const token = raw.trim().toLowerCase();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    out.push(token);
  }
  return out;
}

export function normalizeAnalysisConfig(
  raw: Record<string, unknown> | null | undefined,
  profileId: string
): ReportAnalysisConfig {
  return {
    profileId: String(raw?.profileId ?? raw?.profile_id ?? profileId),
    brandNames: normalizeNameList(raw?.brandNames ?? raw?.brand_names),
    competitorNames: normalizeNameList(raw?.competitorNames ?? raw?.competitor_names),
    accountName:
      typeof raw?.accountName === 'string'
        ? raw.accountName.trim()
        : typeof raw?.account_name === 'string'
          ? raw.account_name.trim()
          : '',
  };
}
